import React, { useMemo } from 'react';
import { ChevronDownIcon } from '@rsos/base-assets/icons';
import { Box, Text } from '@rsos/base-components';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { RedCircle } from '../../CallChats/TF911ChatBox/TF911.styles';
import { CardOverlay } from '../tabs/OverviewTab/OverviewTab.styles';
import { RotatedButton } from './LiveChatCard.styles';

interface LiveChatCardProps {
  callerID: string;
  onClick: () => void;
  isRcs?: boolean;
  isRcsClosed?: boolean;
  source?: string;
  isConversationEnded?: boolean;
}

/**
 * Clickable chat card component that is displayed on the overview tab when it's a live chat.
 */
const LiveChatCard = ({
  callerID,
  onClick,
  isRcs = false,
  isRcsClosed = false,
  source = '',
  isConversationEnded = false,
}: LiveChatCardProps) => {
  const shouldDisplayRedCircle = useMemo(() => {
    if (isRcs && isRcsClosed) return false;
    if (source === SOURCE_TCAPI && isConversationEnded) return false;
    return true;
  }, [isRcs, isRcsClosed, source, isConversationEnded]);

  const displayText = useMemo(() => {
    if (isRcs) {
      if (isRcsClosed) {
        return 'Closed Google RCS';
      }
      return 'Google RCS';
    }
    if (source === SOURCE_TCAPI) {
      if (isConversationEnded) {
        return 'Closed Text-to-911';
      }
      return 'Text-to-911';
    }
    return 'Live Chat';
  }, [isRcs, isRcsClosed, source, isConversationEnded]);

  return (
    <CardOverlay data-name="live-chat-card" onClick={onClick}>
      <Box horizontal justify="space-between" align="center">
        <Box>
          <Box align="center" horizontal marginBottom="2px">
            {shouldDisplayRedCircle && <RedCircle />}
            <Text
              color="primaryText"
              data-name="live-chat"
              fontWeight="bold"
              size="normal"
            >
              {displayText}
            </Text>
          </Box>
          <Text color="secondaryText" data-name="live-chat-number" size="small">
            {callerID}
          </Text>
        </Box>

        <Box width="24px">
          <RotatedButton btnType="secondary" size="xsmall" noMargin>
            <ChevronDownIcon width={10} height={10} />
          </RotatedButton>
        </Box>
      </Box>
    </CardOverlay>
  );
};

export default LiveChatCard;
